import { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { fetchLiveScores } from '../utils/liveScores';

const POLL_INTERVAL_MS = 30000;
const STALE_AFTER_MS = 2 * 60 * 1000;
const SCORE_WINDOW = { days: 7 };

const hasKickedOffRecently = (game, now) => {
  if (!game.dateTime) return false;
  const kickoff = DateTime.fromISO(game.dateTime);
  return kickoff <= now && kickoff >= now.minus(SCORE_WINDOW);
};

export const useLiveScores = (games) => {
  const [liveScores, setLiveScores] = useState({});
  const finishedGameIds = useRef(new Set());

  useEffect(() => {
    let cancelled = false;

    const refresh = async () => {
      if (document.hidden) return;
      const now = DateTime.utc();
      const tracked = games.filter((game) => (
        !finishedGameIds.current.has(game.id) && hasKickedOffRecently(game, now)
      ));
      if (tracked.length === 0) return;

      const scores = await fetchLiveScores(tracked);
      if (cancelled) return;
      const updatedAt = Date.now();
      Object.entries(scores).forEach(([gameId, score]) => {
        if (score.state === 'post') finishedGameIds.current.add(gameId);
      });
      setLiveScores((previous) => {
        const next = { ...previous };
        tracked.forEach(({ id }) => {
          const last = next[id];
          if (scores[id]) {
            next[id] = { ...scores[id], updatedAt, stale: false };
          } else if (last?.state === 'in' && updatedAt - last.updatedAt > STALE_AFTER_MS) {
            next[id] = { ...last, stale: true };
          }
        });
        return next;
      });
    };

    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', refresh);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [games]);

  return liveScores;
};
